import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Button from "../../../../atoms/Button";
import { COLOR_SYSTEM } from "../../../../design/design-system";
import { TocItem, TocKind } from "../tocUtils";

interface TocDropdownProps {
  items: TocItem[];
  onSelect: (item: TocItem) => void;
}

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 200px;
  max-width: 320px;
  max-height: 360px;
  overflow-y: auto;
  padding: 4px 0;
`;

const MenuItem = styled.button<{ $indent: number; $kind: TocKind }>`
  display: block;
  width: 100%;
  padding: 7px 12px;
  padding-left: ${({ $indent }) => $indent}px;
  border: none;
  background: none;
  font-size: ${({ $kind }) => ($kind === "section" ? "13px" : "12.5px")};
  font-weight: ${({ $kind }) =>
    $kind === "section" ? 700 : $kind === "badge" ? 600 : 400};
  color: ${({ $kind }) =>
    $kind === "heading"
      ? COLOR_SYSTEM.get("Gray")?.[600]
      : COLOR_SYSTEM.get("Gray")?.[800]};
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

/**
 * AI 검토안 본문의 마크다운 헤더 목차 드롭다운.
 * 헤더가 0~1개면 목차의 의미가 없어 렌더하지 않는다(숨김).
 */
const TocDropdown: React.FC<TocDropdownProps> = ({ items, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 가장 얕은 depth를 기준(0 들여쓰기)으로 상대 들여쓰기 계산.
  // 섹션마커/badge가 없는 보고서(헤더만)도 첫 줄부터 들여쓰기 없이 시작한다.
  const minDepth = useMemo(
    () => (items.length ? Math.min(...items.map((i) => i.depth)) : 0),
    [items]
  );

  // 바깥 클릭 시 닫힘 (기존 보고서 헤더 드롭다운과 동일 패턴)
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // 헤더가 1개 이하이면 목차 불필요 → 버튼 자체를 숨김
  if (items.length <= 1) return null;

  const handleSelect = (item: TocItem) => {
    onSelect(item);
    setIsOpen(false);
  };

  return (
    <Container ref={containerRef}>
      <Button
        type="Icon"
        size="Small"
        color="Secondary"
        variant="Ghost"
        icon="List"
        onClick={() => setIsOpen((prev) => !prev)}
        title="목차"
        style={{
          height: "28px",
          width: "28px",
          minHeight: "28px",
          minWidth: "28px",
          backgroundColor: isOpen ? COLOR_SYSTEM.get("Gray")?.[100] : "transparent",
        }}
      />
      {isOpen && (
        <Menu>
          {items.map((item) => (
            <MenuItem
              key={item.id}
              $indent={12 + (item.depth - minDepth) * 14}
              $kind={item.kind}
              onClick={() => handleSelect(item)}
              title={item.text}
            >
              {item.text}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Container>
  );
};

export default TocDropdown;
